import { parseCookies } from 'nookies';

import { CRITEO_FPID_HEADER_NAME, readExistingCriteoFpid } from '@/components/entities/analytics/components/CriteoFpid/utils/criteoFpid';
import { getGA4SessionData } from '@/components/entities/analytics/gtm/gtm.session.utils';
import { USER_SOURCE_HEADER_NAMES } from '@/libs/userSource/config';
import {
  readCustomerSourceHeadersFromStorage,
  writeCustomerSourceHeadersToStorage,
} from '@/libs/userSource/customerSourceStorage';
import {
  parseUserSourceHeaders,
  resolveFbcHeaderValue,
} from '@/libs/userSource/parseUserSourceHeaders';
import { sanitizeTrackingValue } from '@/libs/userSource/trackingValue';
import { parseVerifyUserSourceQuery } from '@/libs/userSource/verifyUserSourceQuery';

type TrackingHeaderAdapter = {
  set: (name: string, value: string) => void;
  delete: (name: string) => void;
};

const ALLOWED_CUSTOMER_SOURCE_HEADERS = new Set<string>(USER_SOURCE_HEADER_NAMES);
const GA4_HEADERS_TIMEOUT_MS = 900;

function getCustomerSourceHeaders(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const parsedFromStorage = readCustomerSourceHeadersFromStorage();
    const parsedFromUrl = parseUserSourceHeaders(window.location.href);

    const storageHeaders = Object.entries(parsedFromStorage).reduce<Record<string, string>>((acc, [name, value]) => {
      if (!ALLOWED_CUSTOMER_SOURCE_HEADERS.has(name)) {
        return acc;
      }

      const normalizedValue = sanitizeTrackingValue(value);
      if (normalizedValue) {
        acc[name] = normalizedValue;
      }

      return acc;
    }, {});

    // URL params should always win for the current landing/session start.
    // This removes race conditions where early requests read stale localStorage.
    const merged = {
      ...storageHeaders,
      ...parsedFromUrl,
    };

    const normalizedHeaders = Object.entries(merged).reduce<Record<string, string>>((acc, [name, value]) => {
      if (!ALLOWED_CUSTOMER_SOURCE_HEADERS.has(name)) {
        return acc;
      }

      const normalizedValue = sanitizeTrackingValue(value);
      if (normalizedValue) {
        acc[name] = normalizedValue;
      }

      return acc;
    }, {});

    if (Object.keys(parsedFromUrl).length > 0) {
      writeCustomerSourceHeadersToStorage(normalizedHeaders);
    }

    return normalizedHeaders;
  } catch {
    const parsedFromUrl = parseUserSourceHeaders(window.location.href);
    const normalizedHeaders = Object.entries(parsedFromUrl).reduce<Record<string, string>>((acc, [name, value]) => {
      if (!ALLOWED_CUSTOMER_SOURCE_HEADERS.has(name)) {
        return acc;
      }

      const normalizedValue = sanitizeTrackingValue(value);
      if (normalizedValue) {
        acc[name] = normalizedValue;
      }

      return acc;
    }, {});

    if (Object.keys(normalizedHeaders).length > 0) {
      writeCustomerSourceHeadersToStorage(normalizedHeaders);
    }

    return normalizedHeaders;
  }
}

function getVerifySourceGA4FromUrl() {
  if (typeof window === 'undefined') {
    return {
      shouldVerifyUserSource: false,
      sourceSessionId: null,
      sourceClientId: null,
    };
  }

  const searchParams = new URL(window.location.href).searchParams;
  const { shouldVerifyUserSource, ga4ClickId, ga4SessionId } = parseVerifyUserSourceQuery(searchParams);

  return {
    shouldVerifyUserSource,
    sourceSessionId: ga4SessionId,
    sourceClientId: ga4ClickId,
  };
}

function resolveGA4HeadersForVerifySource(sessionId: string | null, clientId: string | null) {
  const { shouldVerifyUserSource, sourceClientId, sourceSessionId } = getVerifySourceGA4FromUrl();

  if (!shouldVerifyUserSource) {
    return { sessionId, clientId };
  }

  // For verify flow:
  // - if client ids are different, always send client id from source URL;
  // - if they are equal (or GTM missing), any is acceptable, keep resolved value.
  let resolvedClientId = clientId;
  if (sourceClientId) {
    if (clientId && clientId !== sourceClientId) {
      resolvedClientId = sourceClientId;
    } else if (!clientId) {
      resolvedClientId = sourceClientId;
    }
  }

  return {
    sessionId: sessionId || sourceSessionId,
    clientId: resolvedClientId,
  };
}

export const applyTrackingHeaders = async (
  adapter: TrackingHeaderAdapter,
): Promise<void> => {
  // UTM/source headers are persisted in localStorage by CustomerSourceAnalytics.
  // Apply them first. For click-id we keep URL/localStorage value as highest priority.
  const customerSourceHeaders = getCustomerSourceHeaders();
  for (const [name, value] of Object.entries(customerSourceHeaders)) {
    adapter.set(name, value);
  }
  const sourceClickId = customerSourceHeaders['x-click-id'] || null;

  // Cookies (fbc / fbp / ttclid / ttp / twclid / click_id / cid / aclid)
  const cookies = parseCookies();
  const fbp = sanitizeTrackingValue(cookies._fbp);
  const fbc = sanitizeTrackingValue(cookies._fbc);
  const ttclid = sanitizeTrackingValue(cookies.ttclid);
  const ttp = sanitizeTrackingValue(cookies._ttp);
  const twclid = sanitizeTrackingValue(cookies.twclid || cookies._twclid);
  const clickId = sanitizeTrackingValue(cookies.click_id || cookies.clickid);
  const cid = sanitizeTrackingValue(cookies.cid || cookies._cid);
  const aclid = sanitizeTrackingValue(cookies.aclid || cookies._aclid);

  if (fbp) {
    adapter.set('x-fbp', fbp);
  }

  if (fbc) {
    const resolvedFbc = resolveFbcHeaderValue(fbc);
    if (resolvedFbc) {
      adapter.set('x-fbc', resolvedFbc);
    } else {
      adapter.delete('x-fbc');
    }
  }

  if (ttclid) {
    adapter.set('x-ttclid', ttclid);
  }

  if (ttp) {
    adapter.set('x-ttp', ttp);
  }

  if (twclid) {
    adapter.set('x-twclid', twclid);
  }

  if (!sourceClickId && clickId) {
    adapter.set('x-click-id', clickId);
  }

  if (!sourceClickId && cid) {
    adapter.set('x-click-id', cid);
  }

  if (!sourceClickId && aclid) {
    adapter.set('x-click-id', aclid);
  }

  // FPID is a marketing identifier, so do not send a previously stored value
  // to the backend unless Cookiebot currently confirms tracking consent.
  const criteoFpid = typeof window !== 'undefined' && window.Cookiebot?.consent?.statistics
    ? readExistingCriteoFpid()
    : null;
  if (criteoFpid) {
    adapter.set(CRITEO_FPID_HEADER_NAME, criteoFpid);
  }

  // Get GA4 session_id and client_id through GTM.
  // Must not block sending UTM/click headers for too long.
  try {
    const ga4Data = await Promise.race([
      getGA4SessionData(),
      new Promise<{ sessionId: null; clientId: null }>((resolve) => {
        setTimeout(resolve, GA4_HEADERS_TIMEOUT_MS, { sessionId: null, clientId: null });
      }),
    ]);
    const { sessionId, clientId } = resolveGA4HeadersForVerifySource(ga4Data.sessionId, ga4Data.clientId);

    if (sessionId) {
      adapter.set('x-ga4-session-id', sessionId);
    }
    if (clientId) {
      adapter.set('x-ga4-client-id', clientId);
    }
  } catch (e) {
    // If GTM access failed in verify flow, fallback to source GA4 values from URL.
    const { shouldVerifyUserSource, sourceSessionId, sourceClientId } = getVerifySourceGA4FromUrl();
    if (shouldVerifyUserSource) {
      if (sourceSessionId) {
        adapter.set('x-ga4-session-id', sourceSessionId);
      }
      if (sourceClientId) {
        adapter.set('x-ga4-client-id', sourceClientId);
      }
    }

    console.warn('Failed to get GA4 session data from GTM:', e);
  }
};
